import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sessionApi, getAuthSession } from '../services/api';
import '../styles/SessionPaymentReceipt.css';

function SessionPaymentReceipt() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadBookingDetails();
  }, [bookingId]);

  const loadBookingDetails = async () => {
    try {
      const response = await sessionApi.getBookingDetails(bookingId);
      setBooking(response);
      setLoading(false);
    } catch (err) {
      setError('Failed to load booking details');
      setLoading(false);
    }
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const handleDownloadReceipt = async () => {
    try {
      const token = getAuthSession()?.token || localStorage.getItem('authToken') || '';
      const response = await fetch(`/api/sessions/booking/${bookingId}/receipt`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `receipt-${bookingId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Error downloading receipt:', err);
    }
  };

  const handleDone = () => {
    navigate('/client/sessions');
  };

  if (loading) {
    return (
      <div className="receipt-container">
        <div className="spinner">Loading receipt...</div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="receipt-container">
        <div className="error-message">Booking not found</div>
      </div>
    );
  }

  return (
    <div className="receipt-container">
      <div className="receipt-wrapper" id="receipt">
        {/* Header */}
        <div className="receipt-header">
          <div className="receipt-logo">
            <h2>🏥 Mental Health Platform</h2>
          </div>
          <div className="receipt-status success">
            <span className="checkmark">✓</span>
            <h3>BOOKING CONFIRMED</h3>
          </div>
        </div>

        {/* Booking ID & Date */}
        <div className="receipt-info">
          <div className="info-item">
            <label>Booking Reference:</label>
            <span className="booking-id">{bookingId}</span>
          </div>
          <div className="info-item">
            <label>Booking Date:</label>
            <span>{new Date(booking.createdAt).toLocaleString()}</span>
          </div>
          <div className="info-item">
            <label>Payment Status:</label>
            <span className={`status-badge ${booking.paymentStatus}`}>
              {booking.paymentStatus.toUpperCase()}
            </span>
          </div>
          {booking.paymentId && (
            <div className="info-item">
              <label>Payment ID:</label>
              <span>{booking.paymentId}</span>
            </div>
          )}
        </div>

        {/* Session Details */}
        <div className="receipt-section">
          <h4>Session Details</h4>
          <div className="session-details">
            <div className="detail-row">
              <span className="label">Session Title:</span>
              <span className="value">{booking.sessionTitle}</span>
            </div>
            <div className="detail-row">
              <span className="label">Therapist:</span>
              <span className="value">{booking.therapistName}</span>
            </div>
            <div className="detail-row">
              <span className="label">Topic:</span>
              <span className="value">{booking.topic}</span>
            </div>
            <div className="detail-row">
              <span className="label">Session Date & Time:</span>
              <span className="value">{new Date(booking.sessionScheduledAt).toLocaleString()}</span>
            </div>
            <div className="detail-row">
              <span className="label">Mode:</span>
              <span className="value badge-mode">
                {booking.mode === 'online' ? '🌐 Online' : '📍 Offline'}
              </span>
            </div>
            {booking.mode === 'online' && booking.zoomLink && (
              <div className="detail-row">
                <span className="label">Zoom Link:</span>
                <span className="value zoom-link">
                  <a href={booking.zoomLink} target="_blank" rel="noopener noreferrer">
                    Join Meeting →
                  </a>
                </span>
              </div>
            )}
            {booking.mode === 'offline' && booking.location && (
              <div className="detail-row">
                <span className="label">Location:</span>
                <span className="value">{booking.location}</span>
              </div>
            )}
          </div>
        </div>

        {/* Pricing Details */}
        <div className="receipt-section">
          <h4>Payment Details</h4>
          <div className="pricing-details">
            <div className="price-row">
              <span className="label">Session Price:</span>
              <span className="value">₹{booking.price}</span>
            </div>
            <div className="price-row">
              <span className="label">Tax (0%):</span>
              <span className="value">₹0</span>
            </div>
            <div className="price-row total">
              <span className="label">Total Paid:</span>
              <span className="value total-amount">₹{booking.price}</span>
            </div>
            {booking.paymentMethod && (
              <div className="price-row">
                <span className="label">Payment Method:</span>
                <span className="value">{booking.paymentMethod}</span>
              </div>
            )}
          </div>
        </div>

        {/* Important Notes */}
        <div className="receipt-section notes">
          <h4>📌 Important Information</h4>
          <ul>
            <li>✓ Your booking is confirmed. Please save this receipt for your records.</li>
            <li>✓ A reminder will be sent 24 hours before the session.</li>
            {booking.mode === 'online' && (
              <li>✓ Join the session using the Zoom link 5 minutes before the scheduled time.</li>
            )}
            {booking.mode === 'offline' && (
              <li>✓ Arrive 10 minutes early to the session location.</li>
            )}
            <li>✓ If you need to cancel, do so at least 24 hours before the session.</li>
            <li>✓ Cancellation is free if done 24 hours before the session.</li>
          </ul>
        </div>

        {/* Footer */}
        <div className="receipt-footer">
          <p>Thank you for choosing Mental Health Platform</p>
          <p className="contact-info">
            Support: support@mentalhealth.com | Phone: +91-XXXXX-XXXXX
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="receipt-actions no-print">
        <button className="btn-primary" onClick={handlePrintReceipt}>
          🖨️ Print Receipt
        </button>
        <button className="btn-secondary" onClick={handleDownloadReceipt}>
          📥 Download PDF
        </button>
        <button className="btn-success" onClick={handleDone}>
          👉 Continue Shopping
        </button>
      </div>

      {/* Share Receipt */}
      <div className="receipt-share no-print">
        <p>Share this receipt via:</p>
        <button className="share-btn whatsapp" title="Share via WhatsApp">
          💬 WhatsApp
        </button>
        <button className="share-btn email" title="Share via Email">
          📧 Email
        </button>
      </div>
    </div>
  );
}

export default SessionPaymentReceipt;
